import {Injectable} from '@angular/core';
import {FieldConfig} from '../../components/crudbuilder/models/field-config';
import {CrudBuilderConfig} from '../../components/crudbuilder/models/crud-builder-config';
import * as changeCase from 'change-case';
import {ApiService} from '@core/services/api/api.service';
import {catchError, concatMap, map, mergeMap, tap} from 'rxjs/operators';
import {PopularAppStatesService} from '@core/services/popular-app-states/popular-app-states.service';
import {Observable, of, throwError, zip} from 'rxjs';
import {ErrorFormatter} from '@core/helpers/error-formatter'
import {JsonApiEntityService} from "@core/state/jsonentity/json-api-entity.service";

/**
 * Service that offers helpers to help on processing CrudBuilders
 */
@Injectable({
  providedIn: 'root'
})
export class BreadBuilderService {

  constructor(
    public api: ApiService,
    public states: PopularAppStatesService,
  ) {
  }

  /**
   * Process CrudBuilderConfig
   *
   * Process shortcuts/shorthands on the config and generates proper FieldConfig data.
   */
  processCrudBuilderConfig(config: CrudBuilderConfig) {
    // If there is no readFields, basically just use the fields array.
    config.readFields = config.readFields ? config.readFields : config.fields;

    // Process the fields
    config.fields = this.processFields(config.fields);
    config.readFields = this.processFields(config.readFields);
    return config;
  }

  /**
   * Sometimes, people use shortcuts and only type a string name on the fields list.
   * This expands that string into a FieldConfig.
   *
   * Sometimes, the label is not input on the field config.
   * This creates a label from the name, which is basically just converts it into Capital Case.
   */
  processFields(fields) {
    if (!fields) {
      return [];
    }
    const processedFields = [];

    for (let field of fields) {
      if (this.isString(field)) {
        field = new FieldConfig(field, 'string');
      }

      field = this.processFieldLabel(field as FieldConfig);
      processedFields.push(field);
    }

    return processedFields;
  }

  /**
   * This creates a label from the FieldConfig name, which is basically just converts it into Capital Case.
   */
  processFieldLabel(field: FieldConfig) {
    if (field.label === null) {
      field.label = changeCase.capitalCase(field.name)
        .replace(' Configuration Id', '');
    }
    return field;
  }

  isString(s) {
    return typeof s === 'string';
  }

  getBrowseLink(config: CrudBuilderConfig) {
    return '/dashboard/' + config.path;
  }

  getEditLink(config: CrudBuilderConfig, id) {
    return '/dashboard/' + config.path + '/edit/' + id;
  }

  getReadLink(config: CrudBuilderConfig, id) {
    return config.hasCustomBackRoute ? config.getCustomBackRoute() : '/dashboard/' + config.path + '/read/' + id;
  }

  /**
   * Preprocesses a config properly.
   * Basically, a model can be preprocessed manually by the config. In order to make it compatible with the fields.
   * Also, it is processed in a way that it supports relation configs, i.e. relation => relation_configuration_id.
   */
  getEditPreprocessedModel(config: CrudBuilderConfig, data: any) {
    let model;

    if (config.editPreprocess) {
      model = {...config.editPreprocess(data)};
    } else {
      model = {...data};
    }

    model = this.processConfigurationIds(model);
    model = this.processHasOneFields(config, model);

    return model;
  }

  /**
   *
   */
  private processHasOneFields(config: CrudBuilderConfig, model){
    for (const field of config.fields) {
      if (field) {
        if (field.hasOwnProperty('type') && field['type'] == 'hasone') {
          console.log(field);
          if (!model[field['name']]) {
            console.log('here');
            model[field['name']] = {};
          }
        }
      }
    }
    model.nationality = {
      id: 158,
      name: "Nederland",
      value: "NETHERLANDS"
    };
    model.nationality_configuration_id = 158;
    model.email = null;
    console.log(model);
    return model;
  }

  /**
   * For every property of the model.
   * If there is a RelationConfig.
   * Create a [relation_config_name]_configuration_id.
   */
  private processConfigurationIds(model) {
    for (const property in model) {
      if (
        model.hasOwnProperty(property)
        && model[property]
        && model[property].hasOwnProperty('id')
        && model[property].hasOwnProperty('name')
      ) {
        model[property + '_configuration_id'] = model[property].id;
      }
    }

    return model;
  }

  /**
   * Load model to the CrudBuilderConfig.model given CrudBuilderConfig
   * Returns finite observable, so you don't need to unsubscribe.
   */
  loadModelToConfig(config: CrudBuilderConfig) {
    let sub;

    if (config.getEndpoint === 'CUSTOM') {
      sub = config.customGet(this);
    } else {
      sub = this.api.get(config.getEndpoint).pipe(
        map(data => data['data'])

      );
    }

    return config.runOnLoad(this).pipe(
      concatMap(() => {
        return sub.pipe(
          tap((data: any) => {

            // Special case for multiedit loading
            // Load models to config.models
            // instead of model to config.model
            if (config.multiEdit) {
              // config.models = []; // Uncomment this to test if there are no returned models.
              config.models = data;
              config.manyErrors = [];
              for (let i = 0; i < config.models.length; i++) {
                config.models[i] = this.getEditPreprocessedModel(config, config.models[i]);
                config.manyErrors.push(new ErrorFormatter());
              }

              // Initially we show only one model if there are no results from the API
              if (!config.models.length) {
                config.models = [{}];
                config.manyErrors.push(new ErrorFormatter());
              }
            } else {
              config.model = this.getEditPreprocessedModel(config, data);
            }

          }),
        );
      }),
    );
  }

  // No need to load model on live, model has value already and it comes from local storage.
  // Just adding getEditPreprocessedModel
  addPreprocessedModel(config: CrudBuilderConfig) {
    if (config.multiEdit) {
      // config.models = []; // Uncomment this to test if there are no returned models.
      config.manyErrors = [];
      for (let i = 0; i < config.models.length; i++) {
        config.models[i] = this.getEditPreprocessedModel(config, config.models[i]);
        config.manyErrors.push(new ErrorFormatter());
      }

      // Initially we show only one model if there are no results from the API
      if (!config.models.length) {
        config.models = [{}];
        config.manyErrors.push(new ErrorFormatter());
      }
    }
  }

  /**
   * Save model to the API given CrudBuilderConfig data
   * Returns finite observable, so you don't need to unsubscribe.
   */
  saveModelFromConfig(config: CrudBuilderConfig): Observable<any> {
    if (config.multiEdit) {
      return this.saveModelFromConfigMultiEdit(config);
    }

    return this.saveModel(config.model, config).pipe(
      tap(data => {
        config.model = this.getEditPreprocessedModel(config, data);
      }),
    );
  }

  /**
   *
   * @param i For multiEdit configs, the index
   */
  saveModel(model, config, i = 0) {
    console.log(model);
    let sub;

    // TODO: Save HasOne first.
    model = config.runBeforeSaving(model, this);

    if (config.putEndpoint === 'CUSTOM') {
      sub = config.customPut(this);
    } else {

      // Update if already created
      if (model.id) {
        model = config.runBeforeSavingOnEditView(model, this);
        sub = this.api.put(config.putEndpoint + '/' + model.id, model);
      } else {
        // Create if not yet created
        model = config.runBeforeSavingOnEditView(model, this);
        sub = this.api.post(config.putEndpoint, model);
      }

      // Map to the model instead of the ApiResponse
      const originalModel = {...model};
      sub = sub.pipe(
        map(data => data['data']),
        tap(data => { originalModel.id = data['id']; }),
        concatMap((data) => {
          console.log(originalModel);
          return this.saveHasOnes(config, originalModel);
        }),
        catchError(errors => {
          console.log(errors);
          if (config.multiEdit) {
            config.manyErrors[i] = new ErrorFormatter(errors.error);
            return of('FAIL');
          } else {
            return throwError(errors);
          }
        })
      );
    }

    return sub;
  }

  saveModelFromConfigMultiEdit(config: CrudBuilderConfig) {
    // On MultiEdit, we save all the models each, updating or creating as necessary.
    const observableArray = [];
    let i = 0;
    for (const model of config.models) {
      observableArray.push(this.saveModel(model, config, i));
      i++;
    }
    return zip(...observableArray).pipe(
      mergeMap((data: any) => {
        for (const datum of data) {
          if (datum === 'FAIL') {
            return throwError({
              error: {
                message: 'Failed'
              }
            });
          }
        }
        return of({});
      })
    );
  }

  saveHasOnes(main_config, main_model): Observable<any> {
    for (const field of main_config.fields) {
      if (field['type'] === 'hasone') {
        const config = field['data']['config'] as CrudBuilderConfig;
        const model = {...main_model[field['name']]};

        model['_oldData'] = main_model;
        return this.saveModel(model, config);
      }
    }
    return of({});
  }
}
